/**
 * Chat Module – Pinia Store (Pinia 3.0.4)
 *
 * Performance:
 *  - Message cache per room (messagesByRoom)
 *  - Re-Render-Guards: only replace rooms/messages if the CONTENT has actually changed (signature comparison)
 */

import { defineStore } from "pinia";
import { ref, computed, nextTick } from "vue";

import { chatApi, MESSAGE_PAGE_SIZE } from "@/services/chatApi";
import { mapApiMessage, mapApiRoom } from "@/services/chatMapper";
import { useLocationStore } from "@/stores/locations";
import type { ChatMessage, ChatRoom } from "@/types/chat";
import { isApp } from "@/functions/enviroment";

function messagesSignature(
    msgs: { _id: string; seen?: boolean; saved?: boolean; distributed?: boolean }[],
): string {
    // ID + seen + saved + distributed
    return msgs
        .map((m) => `${m._id}:${m.seen ? 1 : 0}:${m.saved ? 1 : 0}:${m.distributed ? 1 : 0}`)
        .join("|");
}

function roomsSignature(rooms: ChatRoom[]): string {
    return rooms
        .map((r) =>
            [
                r.roomId,
                r.unreadCount ?? 0,
                r.lastMessage?.timestamp ?? "",
                r.lastMessage?.content ?? "",
                r.lastMessage?.senderId ?? "",
            ].join(":"),
        )
        .join("|");
}

let pollTimer: ReturnType<typeof setInterval> | undefined;
const POLL_INTERVAL = 10000;

export const useChatStore = defineStore("chat", () => {
    // ---------------------------- States ----------------------------

    const rooms = ref<ChatRoom[]>([]);
    const roomsLoaded = ref(false);

    // cache: roomId -> messages
    const messagesByRoom = ref<Record<string, ChatMessage[]>>({});
    const messagesLoaded = ref(false);

    const activeRoomId = ref<string>("");
    const roomIsOpen = ref(false);
    const roomsListOpened = ref(true);

    // additional flag per room: "End reached?"
    const fullyLoaded = ref<Record<string, boolean>>({});

    // ---------------------------- Getters ----------------------------

    const currentLocationId = computed<string>(
        () => useLocationStore().activeLocation?.id ?? "",
    );

    // messages for active room (from cache)
    const activeMessages = computed<ChatMessage[]>(
        () => messagesByRoom.value[activeRoomId.value] ?? [],
    );

    const activeRoom = computed<ChatRoom | undefined>(() =>
        rooms.value.find((r) => r.roomId === activeRoomId.value),
    );

    const totalUnread = computed<number>(() =>
        rooms.value.reduce((sum, r) => sum + (r.unreadCount ?? 0), 0),
    );

    // ---------------------------- Internal helpers ----------------------------

    function setRoomsIfChanged(next: ChatRoom[]): void {
        if (roomsSignature(next) !== roomsSignature(rooms.value)) {
            rooms.value = next;
        }
    }

    function setMessagesIfChanged(
        roomId: string,
        next: ChatMessage[],
    ): boolean {
        const current = messagesByRoom.value[roomId] ?? [];
        if (messagesSignature(next) !== messagesSignature(current)) {
            messagesByRoom.value = { ...messagesByRoom.value, [roomId]: next };
            return true; // has changed
        }
        return false;
    }

    // number of messages existing on the server (excluding optimistic ones) */
    function serverMessageCount(roomId: string): number {
        return (messagesByRoom.value[roomId] ?? []).filter(
            (m) => !m._id.startsWith("temp_"),
        ).length;
    }

    // ---------------------------- Actions ----------------------------

    // get all location rooms
    async function loadRooms(): Promise<void> {
        const locationId = currentLocationId.value;
        if (!locationId) return;
        roomsLoaded.value = false;
        try {
            const apiRooms = await chatApi.getRooms(locationId);
            setRoomsIfChanged(apiRooms.map(mapApiRoom));
        } finally {
            roomsLoaded.value = true;
        }
    }

    /**
     * Load a room's history (initial load / reload)
     * Uses the cache; only replaces the content if there has been a real change.
     * @returns true if the content has changed
     */

    async function loadMessages(room: ChatRoom): Promise<boolean> {
        messagesLoaded.value = false;
        try {
            const apiMsgs = await chatApi.getMessages(
                room.locationId,
                room.supplierId,
            );
            const mapped = apiMsgs.map((m) =>
                mapApiMessage(m, currentLocationId.value, room.supplierId),
            );
            const changed = setMessagesIfChanged(room.roomId, mapped);

            // less than one full page -> there are no older entries
            fullyLoaded.value[room.roomId] = apiMsgs.length < MESSAGE_PAGE_SIZE;
            messagesLoaded.value = fullyLoaded.value[room.roomId];
            return changed;
        } catch {
            messagesLoaded.value = true;
            return false;
        }
    }

    /**
     * Poll Refresh: Appends ONLY new (more recent) messages without overwriting the
     * history that has already been loaded (including older messages retrieved via pagination).
     * @returns true if new messages were added
     */
    async function refreshMessages(room: ChatRoom): Promise<boolean> {
        const current = messagesByRoom.value[room.roomId] ?? [];
        const apiMsgs = await chatApi.getMessages(
            room.locationId,
            room.supplierId,
        );
        const mapped = apiMsgs.map((m) =>
            mapApiMessage(m, currentLocationId.value, room.supplierId),
        );

        const knownIds = new Set(current.map((m) => m._id));

        // only truly new messages (ones that don't exist yet)
        const fresh = mapped.filter((m) => !knownIds.has(m._id));

        // additionally: update the “seen/saved” status of existing messages
        // (e.g., when the server has acknowledged an optimistic message)
        const updatedCurrent = current.map((m) => {
            const server = mapped.find((s) => s._id === m._id);
            return server
                ? { ...m, seen: server.seen, saved: server.saved, distributed: server.distributed }
                : m;
        });

        if (!fresh.length) {
            // apply status changes if necessary (guarded)
            return setMessagesIfChanged(room.roomId, updatedCurrent);
        }

        // append new messages to the end; the history is preserved
        messagesByRoom.value = {
            ...messagesByRoom.value,
            [room.roomId]: [...updatedCurrent, ...fresh],
        };
        return true;
    }

    async function loadOlderMessages(room: ChatRoom): Promise<void> {
        if (fullyLoaded.value[room.roomId]) return;

        const current = messagesByRoom.value[room.roomId] ?? [];
        if (!current.length) return;

        messagesLoaded.value = false;
        try {
            const skip = serverMessageCount(room.roomId);
            const older = await chatApi.getMessages(
                room.locationId,
                room.supplierId,
                skip,
            );

            if (older.length) {
                const knownIds = new Set(current.map((m) => m._id));
                const mapped = older
                    .map((m) => mapApiMessage(m, currentLocationId.value, room.supplierId))
                    .filter((m) => !knownIds.has(m._id)); // deduplication as a safety net

                if (mapped.length) {
                    messagesByRoom.value = {
                        ...messagesByRoom.value,
                        [room.roomId]: [...mapped, ...current],
                    };
                }
            }

            fullyLoaded.value[room.roomId] = older.length < MESSAGE_PAGE_SIZE;
            messagesLoaded.value = fullyLoaded.value[room.roomId];
        } catch {
            messagesLoaded.value = true;
        }
    }

    // mark unread messages from others in the chat room as read
    async function markRoomRead(room: ChatRoom): Promise<void> {
        const current = messagesByRoom.value[room.roomId] ?? [];
        const unread = current
            .filter((m) => m.senderId !== currentLocationId.value && !m.seen)
            .map((m) => m._id);
        if (!unread.length) return;

        await chatApi.markAsRead(room.locationId, room.supplierId, unread);

        // update the local cache so that badges are correct right away
        messagesByRoom.value = {
            ...messagesByRoom.value,
            [room.roomId]: current.map((m) =>
                unread.includes(m._id) ? { ...m, seen: true } : m,
            ),
        };

        // set the room's unreadCount to 0 locally
        setRoomsIfChanged(
            rooms.value.map((r) =>
                r.roomId === room.roomId ? { ...r, unreadCount: 0 } : r,
            ),
        );
    }

    async function openRoom(room: ChatRoom): Promise<void> {
        if (isApp() && window.innerWidth < 900 /* Tablet guard */) { roomsListOpened.value = false; }
        activeRoomId.value = room.roomId;
        roomIsOpen.value = true;
        await loadMessages(room);
        await markRoomRead(room);
    }

    // return to overview (list of chats)
    function closeRoom(): void {
        roomIsOpen.value = false;
        activeRoomId.value = "";
        messagesLoaded.value = true;
        roomsListOpened.value = true; // force component back to the list
    }

    // check or create room and open it
    async function ensureAndOpen(supplierId: string): Promise<ChatRoom> {
        const locationId = currentLocationId.value;
        const apiRoom = await chatApi.ensureRoom(locationId, supplierId);
        const room = mapApiRoom(apiRoom);

        if (!rooms.value.some((r) => r.roomId === room.roomId)) {
            rooms.value = [room, ...rooms.value];
        }

        await nextTick(); // wait until the `rooms` prop has reached the component
        await openRoom(room);

        return room;
    }

    // send message (Optimistic UI); returns true on success
    async function sendMessage(
        text: string,
        files: File[] = [],
    ): Promise<boolean> {
        const room = activeRoom.value;
        if (!room) return false;
        if (!text && !files.length) return false;

        const tempId = `temp_${Date.now()}`;
        const now = new Date();
        const attachments = files.map((file) => ({
            name: file.name.replace(/\.[^/.]+$/, ""),
            extension: file.name.split(".").pop()?.toLowerCase() ?? "",
            type: file.type,
            preview: URL.createObjectURL(file),
            url: URL.createObjectURL(file),
        }));
        const optimistic: ChatMessage = {
            _id: tempId,
            content: text,
            senderId: currentLocationId.value,
            date: now.toLocaleDateString("de-DE", {
                day: "numeric",
                month: "long",
                year: "numeric",
            }),
            timestamp: now.toLocaleTimeString("de-DE", {
                hour: "2-digit",
                minute: "2-digit",
            }),
            saved: false,
            files: attachments
        };

        const current = messagesByRoom.value[room.roomId] ?? [];
        messagesByRoom.value = {
            ...messagesByRoom.value,
            [room.roomId]: [...current, optimistic],
        };

        try {
            const saved = await chatApi.sendMessage(
                room.locationId,
                room.supplierId,
                text,
                files,
            );
            const savedMsg = mapApiMessage(saved, currentLocationId.value, room.supplierId);
            const list = messagesByRoom.value[room.roomId] ?? [];
            messagesByRoom.value = {
                ...messagesByRoom.value,
                [room.roomId]: list.map((m) =>
                    m._id === tempId ? savedMsg : m,
                ),
            };
            await loadRooms(); // update overview / message order
            return true;
        } catch {
            const list = messagesByRoom.value[room.roomId] ?? [];
            messagesByRoom.value = {
                ...messagesByRoom.value,
                [room.roomId]: list.map((m) =>
                    m._id === tempId
                        ? { ...m, saved: false, distributed: false }
                        : m,
                ),
            };
            return false;
        }
    }

    /**
     * A poll run. Always updates the overview; reloads the open
     * room only if it is visible, and highlights new messages from others
     */

    async function pollActive(): Promise<void> {
        await loadRooms();

        if (!roomIsOpen.value) return;
        const room = activeRoom.value;
        if (!room) return;

        const before = messagesByRoom.value[room.roomId] ?? [];
        const knownIds = new Set(before.map((m) => m._id));

        const changed = await refreshMessages(room); // <-- merge statt replace
        if (!changed) return;

        const after = messagesByRoom.value[room.roomId] ?? [];
        const hasNewIncoming = after.some(
            (m) =>
                !knownIds.has(m._id) &&
                m.senderId !== currentLocationId.value &&
                !m.seen,
        );
        if (hasNewIncoming) await markRoomRead(room);
    }

    async function pollOnce(): Promise<void> {
        if (typeof document !== "undefined" && document.hidden) return;
        try {
            await pollActive();
        } catch {
            // silent error; the next pass will try again
        }
    }

    function startPolling(): void {
        if (pollTimer) return;
        void pollOnce();
        pollTimer = setInterval(pollOnce, POLL_INTERVAL);
    }

    function stopPolling(): void {
        if (pollTimer) {
            clearInterval(pollTimer);
            pollTimer = undefined;
        }
    }

    // FCM push for a specific roomId: Overview + reload open room if necessary
    async function handlePush(pushRoomId: string): Promise<void> {
        await loadRooms();
        if (roomIsOpen.value && pushRoomId === activeRoomId.value) {
            const room = activeRoom.value;
            if (room) {
                const changed = await refreshMessages(room);
                if (changed) await markRoomRead(room);
            }
        }
    }

    return {
        // state
        rooms,
        roomsLoaded,
        messagesByRoom,
        messagesLoaded,
        activeRoomId,
        roomIsOpen,
        roomsListOpened,
        // getters
        currentLocationId,
        activeMessages,
        activeRoom,
        totalUnread,
        // actions
        loadRooms,
        loadMessages,
        loadOlderMessages,
        markRoomRead,
        openRoom,
        closeRoom,
        ensureAndOpen,
        sendMessage,
        pollActive,
        startPolling,
        stopPolling,
        handlePush,
    };
});
